import {
  IconArrowLeft,
  IconArrowRight,
  IconCheck,
  IconClock,
  IconDeviceFloppy,
  IconMoodSad,
  IconRobot,
  IconSparkles,
  IconTag,
  IconX,
} from "@tabler/icons-react";
import classes from "../../testRedesign.module.css";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  submitTalentImprovementTest,
  talentProfile,
} from "../../../../../services/talentCalls";
import { errorHandler, toastSuccess } from "../../../../../utils";
import { useUserContext } from "../../../../../context";
import { useNavigate } from "react-router";
import useCountdown from "../../../../../hooks/useCountdown";
import { formatAnswers } from "../../../../../utils/functions";

const MAX_CHARACTERS = 700;
const TIMER_DURATION = 30 * 60; // 30 minutes in seconds

const SCENARIO_TAGS = [
  "Prioritisation", "Stakeholder conflict", "Ambiguity", "Communication", "Judgement",
  "Data quality", "Influence", "Trade-offs", "Pressure", "Ethics",
];

const formatClock = (secs) => {
  const m = Math.floor(Math.max(0, secs) / 60);
  const s = Math.max(0, secs) % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
};

const TestModalComponent = ({
  test,
  setTest,
  setModal,
  attempt,
  setAttempt,
  handleGetImprovementAnswers,
  loading,
  setLoading,
}) => {
  const { data, setData } = useUserContext();

  const [testCompleted, setTestCompleted] = useState(false);
  const [stage, setStage] = useState(1);
  const [forms, setForms] = useState({
    answers: {},
  });
  const debounceRef = useRef(null);

  const navigate = useNavigate();

  const { timeLeft, clearTimer, countEnded } = useCountdown({
    count: TIMER_DURATION,
    startCountdown: !testCompleted && test?.questions?.length > 0,
    id: data?.id,
  });

  const total = test?.questions?.length || 0;
  const currentQuestion = test?.questions?.[stage - 1];
  const pct = total ? Math.round((stage / total) * 100) : 0;
  const scenarioTag = SCENARIO_TAGS[(stage - 1) % SCENARIO_TAGS.length];

  const handleSubmit = useCallback(
    async (action = "submit") => {
      if (
        !countEnded &&
        action === "submit" &&
        stage < (test?.questions?.length || 0)
      ) {
        return setStage((prev) => prev + 1);
      }

      if (!test || !data) return;

      if (action === "submit") setLoading(true);
      try {
        // Format the answers
        const formattedAnswers = formatAnswers(test, forms?.answers);

        const payload = {
          formattedAnswers: formattedAnswers,
          answers: forms?.answers,
          talentId: test?.talentId || data?._id,
          improvementTestId: test?._id,
          skills: test?.skills || [],
        };

        const res = await submitTalentImprovementTest(payload, attempt?._id);

        if (res?.data?.success && action === "submit") {
          toastSuccess("Test submitted successfully");
          setModal(false);
          setTimeout(() => {
            handleGetImprovementAnswers();
          }, 2000);

          setTestCompleted(true);
          const res = await talentProfile();
          if (res?.data.data.talent) {
            setData(res.data.data.talent);
          }
        }
      } catch (error) {
        if (action === "submit") {
          errorHandler(error?.response?.data?.errors, "Error");
        }
      } finally {
        if (action === "submit") setLoading(false);
      }
    },
    [countEnded, data, forms, navigate, setData, stage, test],
  );

  const handleAnswerChange = (value) => {
    setForms((prevState) => ({
      ...prevState,
      answers: {
        ...prevState.answers,
        [currentQuestion?.id]: value,
      },
    }));
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => handleSubmit("save"), 700);
  };

  useEffect(() => {
    if (testCompleted) {
      clearTimer();
    }
  }, [clearTimer]);
  useEffect(() => {
    if (countEnded && !testCompleted && test?.questions?.length > 0) {
      handleSubmit();
    }
  }, [countEnded, testCompleted, test, handleSubmit]);

  return (
    <div className="max-w-[1000px] w-full relative">
      {!loading && !total ? (
        <div className={classes.card}>
          <div className={classes.centerState}>
            <div className={classes.illustrationWrap}>
              <IconMoodSad size={48} />
            </div>
            <div className={classes.centerMessage}>
              Sorry there are no tests available for this role
            </div>
            <button
              className={classes.btnAmber}
              onClick={() => navigate("/talent-report")}
            >
              Contact Support
            </button>
          </div>
        </div>
      ) : (
        <div className={`${classes.card} relative`}>
          {!testCompleted && (
            <button
              type="button"
              className={classes.cardCloseBtn}
              onClick={() => setModal(false)}
              aria-label="Close"
            >
              <IconX size={15} />
            </button>
          )}

          {testCompleted ? (
            <div className={classes.centerState}>
              <div className={`${classes.illustrationWrap} ${classes.illustrationWrapDone}`}>
                <IconCheck size={48} />
              </div>
              <div className={classes.centerMessage}>All done — thank you!</div>
              <button
                className={classes.btnAmber}
                onClick={() => navigate("/talent-profile")}
              >
                OK
              </button>
            </div>
          ) : (
            <>
              <div className={classes.qHeadRow}>
                {test?.skills?.length > 0 && (
                  <div className={classes.qTag}>
                    <IconSparkles size={13} /> {test.skills.join(", ")}
                  </div>
                )}
                <span className={`${classes.progressTimer} ${timeLeft <= 60 ? classes.progressTimerLow : ""}`}>
                  <IconClock size={12} /> {formatClock(timeLeft)}
                </span>
              </div>

              <div className={classes.progressWrap}>
                <div className={classes.progressMeta}>
                  <span className={classes.progressCount}>
                    Question {stage} of {total}
                  </span>
                  <span className={classes.progressPct}>{pct}%</span>
                </div>
                <div className={classes.track}>
                  <div className={classes.trackFill} style={{ width: `${pct}%` }} />
                </div>
              </div>

              <div className={classes.questionView}>
                <div className={classes.qTag}>
                  <IconTag size={13} /> {scenarioTag}
                </div>
                <div className={classes.qSender}>
                  <span className={classes.qAvatar}>
                    <IconRobot size={20} />
                  </span>
                  <div>
                    <span className={classes.qSenderName}>
                      Awasource AI <span className={classes.qBadge}><IconCheck size={9} /></span>
                    </span>
                    <div className={classes.qSenderSub}>Question {stage}</div>
                  </div>
                </div>
                <div className={classes.qText}>{currentQuestion?.text}</div>

                <label className={classes.answerLabel} htmlFor="testModalAnswer">
                  Your response
                </label>

                <textarea
                  id="testModalAnswer"
                  className={classes.textarea}
                  placeholder="Type how you would actually handle this…"
                  maxLength={MAX_CHARACTERS}
                  value={forms?.answers?.[currentQuestion?.id] || ""}
                  onChange={(e) => handleAnswerChange(e.target.value)}
                />

                <div className={classes.actions}>
                  <span className={classes.savedHint}>
                    <IconDeviceFloppy size={13} /> Saved automatically
                  </span>
                  <div className={classes.actionsRight}>
                    {stage > 1 && (
                      <button
                        className={classes.btnGhost}
                        onClick={() => {
                          clearTimeout(debounceRef.current);
                          setStage((prev) => prev - 1);
                        }}
                      >
                        <IconArrowLeft size={15} /> Back
                      </button>
                    )}
                    <button
                      className={classes.btnAmber}
                      disabled={loading || !forms?.answers?.[currentQuestion?.id]}
                      onClick={() => {
                        clearTimeout(debounceRef.current);
                        handleSubmit();
                      }}
                    >
                      {stage < total ? (
                        <>Next <IconArrowRight size={15} /></>
                      ) : (
                        <>Submit <IconCheck size={15} /></>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default TestModalComponent;
